import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { required } from "ra-core";
import { DateTimeInput } from "@/components/admin";
import { useWatch } from "react-hook-form";

import { contactOptionText } from "../misc/ContactOption";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Sale } from "../types";
import { defaultTaskType } from "./taskDefaults";
import { TaskKindInput } from "./TaskKindInput";
import { getTaskKind, type TaskKind } from "./taskKind";
import { formatPersonName } from "../misc/personName";

const saleOptionText = (choice: Sale) => formatPersonName(choice);

/**
 * Fields of the task create/edit forms.
 *
 * `contactLocked`: the task is created from a contact page, so it is a client
 * task for that contact: neither the kind toggle nor the contact selector is
 * shown. Otherwise the kind toggle decides between the contact selector
 * (Proyecto / Cliente) and the assignee selector (Interna).
 */
export const TaskFormContent = ({
  contactLocked,
}: {
  contactLocked?: boolean;
}) => {
  const { taskTypes } = useConfigurationContext();
  const kindValue = useWatch({ name: "kind" }) as TaskKind | undefined;
  const contactId = useWatch({ name: "contact_id" });
  const kind = getTaskKind({ kind: kindValue, contact_id: contactId });

  return (
    <div className="flex flex-col gap-4">
      {contactLocked ? null : <TaskKindInput />}
      <TextInput
        autoFocus
        source="text"
        validate={required()}
        multiline
        className="m-0"
        helperText={false}
      />
      {!contactLocked && kind === "client" ? (
        <ReferenceInput source="contact_id" reference="contacts_summary">
          <AutocompleteInput
            label="resources.tasks.fields.contact_id"
            optionText={contactOptionText}
            helperText={false}
            validate={required("resources.tasks.kind.contact_required")}
            modal
          />
        </ReferenceInput>
      ) : null}
      {kind === "internal" ? (
        <ReferenceInput
          source="sales_id"
          reference="sales"
          sort={{ field: "first_name", order: "ASC" }}
          filter={{ "disabled@neq": true }}
        >
          <SelectInput
            label="resources.tasks.kind.assignee"
            optionText={saleOptionText}
            helperText={false}
            validate={required("resources.tasks.kind.assignee_required")}
          />
        </ReferenceInput>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DateTimeInput
          source="due_date"
          helperText={false}
          validate={required()}
        />
        <SelectInput
          source="type"
          validate={required()}
          choices={taskTypes}
          optionText="label"
          optionValue="value"
          defaultValue={defaultTaskType(taskTypes)}
          helperText={false}
        />
      </div>
    </div>
  );
};
